import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import { Outlet, useParams } from 'react-router-dom';
import PortalLogin from './portalLogin';
import PortalLayout from './portalLayout';
import LoaderBrand from '../ui/progress/loaderBrand';
import {
  clearPortalToken,
  getInfo,
  getSession,
  logout as logoutRequest,
  portalAccessSessionKey,
  portalErrorMessage,
  portalSessionKey,
  readPortalToken,
  setActivePortalEvent,
  writePortalToken
} from '../../features/portal/portalService';

const CenteredBox = ({ children }) => (
  <Box
    minHeight="100vh"
    display="flex"
    alignItems="center"
    justifyContent="center"
    bgcolor="background.default"
    px={2}
    py={4}>
    <Container maxWidth="xs" disableGutters>
      {children}
    </Container>
  </Box>
);

const PortalGate = ({ kind }) => {
  const { eventId, venueId } = useParams();
  const scopedVenueId = kind === 'club' ? venueId : null;
  const hasScope = Boolean(eventId) && (kind !== 'club' || Boolean(venueId));
  const scopeKey = useMemo(
    () => (hasScope ? `${kind}:${eventId}:${scopedVenueId || ''}` : ''),
    [hasScope, kind, eventId, scopedVenueId]
  );

  const legacyKey = useMemo(
    () =>
      hasScope
        ? portalSessionKey({ eventId, kind, venueId: scopedVenueId })
        : '',
    [eventId, kind, scopedVenueId, hasScope]
  );

  const [info, setInfo] = useState(null);
  const [infoStatus, setInfoStatus] = useState('loading');
  const [infoError, setInfoError] = useState('');
  const [token, setToken] = useState('');
  const [tokenKey, setTokenKey] = useState('');
  const [loginToken, setLoginToken] = useState(null);
  const [status, setStatus] = useState('loading');
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const validatedSignature = useRef('');

  // Every portal data call picks up this event header. The route owns the
  // active event; the layout effect runs before the routed page's effects.
  useLayoutEffect(() => {
    setActivePortalEvent(hasScope ? eventId : '');
    return () => setActivePortalEvent('');
  }, [hasScope, eventId]);

  // The public info call first: a link can be dead before any token is
  // considered, and the login screen needs the shared/event-only copy.
  useEffect(() => {
    if (!hasScope) {
      setInfo(null);
      setInfoStatus('invalid');
      return undefined;
    }

    let cancelled = false;
    setInfo(null);
    setInfoStatus('loading');
    setInfoError('');

    getInfo({ eventId, kind, venueId: scopedVenueId })
      .then((data) => {
        if (cancelled) return;
        setInfo({ ...data, scopeKey });
        setInfoStatus('ready');
      })
      .catch((requestError) => {
        if (cancelled) return;

        const httpStatus = requestError?.response?.status;
        if (httpStatus === 404 || httpStatus === 410) {
          setInfoStatus('invalid');
          return;
        }

        setInfoError(portalErrorMessage(requestError, 'The portal could not be loaded.'));
        setInfoStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [hasScope, eventId, kind, scopedVenueId, scopeKey, retry]);

  useEffect(() => {
    if (!hasScope) {
      setStatus('invalid');
      return undefined;
    }

    if (infoStatus === 'invalid') {
      setStatus('invalid');
      return undefined;
    }

    if (infoStatus === 'error') {
      setStatus('error');
      return undefined;
    }

    if (infoStatus !== 'ready' || !info || info.scopeKey !== scopeKey) {
      setStatus('loading');
      return undefined;
    }

    const candidates = [];
    const seenTokens = new Set();
    const addCandidate = (key, value) => {
      if (!key || !value || seenTokens.has(value)) return;
      seenTokens.add(value);
      candidates.push({ key, token: value });
    };

    const accessKey = info.accessId ? portalAccessSessionKey(info.accessId) : '';

    if (loginToken && loginToken.scopeKey === scopeKey && loginToken.token) {
      addCandidate(
        loginToken.accessId ? portalAccessSessionKey(loginToken.accessId) : legacyKey,
        loginToken.token
      );
    }

    if (token && tokenKey) addCandidate(tokenKey, token);
    addCandidate(accessKey, readPortalToken(accessKey));
    addCandidate(legacyKey, readPortalToken(legacyKey));

    const signature = `${scopeKey}|${retry}|${candidates
      .map((candidate) => candidate.token)
      .sort()
      .join(',')}`;

    if (validatedSignature.current === signature) return undefined;
    validatedSignature.current = signature;

    if (candidates.length === 0) {
      setToken('');
      setTokenKey('');
      setLoginToken(null);
      setSession(null);
      setStatus('login');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');
    setError('');

    (async () => {
      for (const candidate of candidates) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const data = await getSession(candidate.token, eventId, { silentExpiry: true });
          if (cancelled) return;

          const scope = data?.scope;
          const scopeMatches =
            scope?.kind === kind &&
            (kind !== 'club' || String(scope?.venueId) === String(venueId)) &&
            (!data?.event?.id || String(data.event.id) === String(eventId));

          if (!scopeMatches) {
            clearPortalToken(candidate.key);
            continue;
          }

          const canonicalKey = data?.accessId
            ? portalAccessSessionKey(data.accessId)
            : candidate.key;
          writePortalToken(canonicalKey, candidate.token);
          candidates.forEach((entry) => {
            if (entry.key !== canonicalKey) clearPortalToken(entry.key);
          });
          if (canonicalKey !== legacyKey) clearPortalToken(legacyKey);

          setToken(candidate.token);
          setTokenKey(canonicalKey);
          setLoginToken(null);
          setSession(data);
          setStatus('ready');
          return;
        } catch (requestError) {
          if (cancelled) return;

          const httpStatus = requestError?.response?.status;

          if (httpStatus === 401) {
            clearPortalToken(candidate.key);
            continue;
          }

          if (httpStatus === 404 || httpStatus === 410) {
            // The token may still be valid on another event URL, so it is not
            // cleared here.
            setStatus('invalid');
            return;
          }

          setError(portalErrorMessage(requestError, 'The portal could not be loaded.'));
          setStatus('error');
          return;
        }
      }

      if (cancelled) return;
      setToken('');
      setTokenKey('');
      setLoginToken(null);
      setSession(null);
      setStatus('login');
    })();

    return () => {
      cancelled = true;
    };
  }, [
    hasScope,
    infoStatus,
    info,
    scopeKey,
    eventId,
    kind,
    venueId,
    legacyKey,
    retry,
    loginToken,
    token,
    tokenKey
  ]);

  const clearStoredTokens = useCallback(() => {
    if (info?.accessId) clearPortalToken(portalAccessSessionKey(info.accessId));
    if (loginToken?.accessId) clearPortalToken(portalAccessSessionKey(loginToken.accessId));
    if (tokenKey) clearPortalToken(tokenKey);
    if (legacyKey) clearPortalToken(legacyKey);
  }, [info, loginToken, tokenKey, legacyKey]);

  useEffect(() => {
    const handleExpired = () => {
      clearStoredTokens();
      setToken('');
      setTokenKey('');
      setLoginToken(null);
      setSession(null);
      setStatus('login');
    };

    window.addEventListener('portal-session-expired', handleExpired);
    return () => window.removeEventListener('portal-session-expired', handleExpired);
  }, [clearStoredTokens]);

  const handleLoggedIn = useCallback(
    (data) => {
      if (!data?.token) return;

      const canonicalKey = data.accessId
        ? portalAccessSessionKey(data.accessId)
        : legacyKey;
      writePortalToken(canonicalKey, data.token);
      if (canonicalKey !== legacyKey) clearPortalToken(legacyKey);

      setToken(data.token);
      setTokenKey(canonicalKey);
      setLoginToken({
        token: data.token,
        accessId: data.accessId || info?.accessId || null,
        scopeKey
      });
      setStatus('loading');
    },
    [legacyKey, info, scopeKey]
  );

  const handleInvalid = useCallback(() => setStatus('invalid'), []);

  const handleLogout = useCallback(async () => {
    try {
      if (token) await logoutRequest(token);
    } catch {
      // Clearing the device is the part the official controls; a failed
      // network call must not leave them signed in.
    } finally {
      clearStoredTokens();
      setToken('');
      setTokenKey('');
      setLoginToken(null);
      setSession(null);
      setStatus('login');
    }
  }, [token, clearStoredTokens]);

  const scopedInfo = info && info.scopeKey === scopeKey ? info : null;
  const activeSession =
    session && (!eventId || String(session.event?.id) === String(eventId))
      ? session
      : null;

  if (!hasScope || infoStatus === 'invalid' || status === 'invalid') {
    return (
      <CenteredBox>
        <Paper sx={{ p: 3 }} elevation={3}>
          <Typography variant="h6" gutterBottom>
            This portal link is not available
          </Typography>
          <Typography color="text.secondary">
            The event or club may have been removed. Ask the tournament organiser for a new link.
          </Typography>
        </Paper>
      </CenteredBox>
    );
  }

  if (infoStatus === 'error' || status === 'error') {
    return (
      <CenteredBox>
        <Paper sx={{ p: 3 }} elevation={3}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {infoError || error}
          </Alert>
          <Button variant="contained" onClick={() => setRetry((value) => value + 1)}>
            Try again
          </Button>
        </Paper>
      </CenteredBox>
    );
  }

  if (status === 'login' && scopedInfo) {
    return (
      <PortalLogin
        info={scopedInfo}
        eventId={eventId}
        kind={kind}
        venueId={scopedVenueId}
        onLoggedIn={handleLoggedIn}
        onInvalid={handleInvalid}
      />
    );
  }

  if (status !== 'ready' || !scopedInfo || !activeSession) {
    return (
      <CenteredBox>
        <Stack alignItems="center" spacing={3}>
          <LoaderBrand width={176} />
          <CircularProgress />
        </Stack>
      </CenteredBox>
    );
  }

  return (
    <PortalLayout session={activeSession} token={token} onLogout={handleLogout}>
      <Outlet
        key={activeSession.event.id}
        context={{
          token,
          session: activeSession,
          portalKey: tokenKey,
          accessId: activeSession.accessId,
          eventId: activeSession.event.id
        }}
      />
    </PortalLayout>
  );
};

export default PortalGate;
